// trendLine.js
// Pivot-based trend line — pure computation, no chart code here
//
// Concept:
//   A Pivot Low  — candle whose low  is lowest  among N candles on each side
//   A Pivot High — candle whose high is highest among N candles on each side
//
// Line behavior (Supertrend style — one line, switches side):
//   Starting state determined by first confirmed pivot type.
//   If last confirmed pivot was a LOW  → show pivot lows  (support, below candles)
//   If last confirmed pivot was a HIGH → show pivot highs (resistance, above candles)
//   Line is flat at last confirmed pivot level until next pivot of same type confirmed.
//   Last segment extends to current candle (open ended).
//
// Lag:
//   A pivot at candle X is confirmed only after N candles to the right are seen.
//   This lag of N candles is unavoidable — cannot know pivot until neighbors seen.
//
// Input:
//   candles — [{time, open, high, low, close}]  (already in chart time unix seconds)
//   n       — lookback/lookforward count (default 3). Higher = fewer but stronger pivots.
//
// Output: {
//   linePoints:   [{time, value}]              — for LineSeries.setData()
//   swingPoints:  [{time, value, type}]        — 'PIVOT_LOW' | 'PIVOT_HIGH' (for markers)
//   currentState: 'uptrend' | 'downtrend' | 'neutral'
// }
//
// To change rules — edit ONLY this file.
// ============================================================

export function computeTrendLine(candles, n = 3) {
  const linePoints  = [];
  const swingPoints = [];

  if (!candles || candles.length < 2 * n + 1) {
    return { linePoints, swingPoints, currentState: 'neutral' };
  }

  // ── pushPoint — prevents duplicate timestamps ──
  const pushPoint = (time, value) => {
    if (linePoints.length > 0 && linePoints[linePoints.length - 1].time === time) {
      linePoints[linePoints.length - 1].value = value;
    } else {
      linePoints.push({ time, value });
    }
  };

  // ── State ──
  let currentLevel  = null;   // current flat line level
  let currentType   = null;   // 'LOW' or 'HIGH' — which side line is on
  let lastPivotLow  = null;   // last pivot-low value  (for higher-low test)
  let lastPivotHigh = null;   // last pivot-high value (for lower-high test)

  // ── MAIN LOOP ──
  // Start from index N so we can look N candles back.
  // Pivot at index i is confirmed when we reach index i+N.
  // So we check pivot at index (i - N) when processing index i.

  for (let i = n; i < candles.length; i++) {
    const pivotIndex = i - n;   // candle we are checking as potential pivot
    const c = candles[pivotIndex];

    // ── Check Pivot Low ──
    // c.low must be <= all lows in [pivotIndex-n .. pivotIndex+n]
    let isPivotLow = true;
    for (let j = pivotIndex - n; j <= pivotIndex + n; j++) {
      if (j === pivotIndex) continue;
      if (j < 0 || j >= candles.length) continue;
      if (candles[j].low < c.low) {
        isPivotLow = false;
        break;
      }
    }

    // ── Check Pivot High ──
    // c.high must be >= all highs in [pivotIndex-n .. pivotIndex+n]
    let isPivotHigh = true;
    for (let j = pivotIndex - n; j <= pivotIndex + n; j++) {
      if (j === pivotIndex) continue;
      if (j < 0 || j >= candles.length) continue;
      if (candles[j].high > c.high) {
        isPivotHigh = false;
        break;
      }
    }

    // ── Update state if pivot confirmed ──
    if (isPivotLow && isPivotHigh) {
      // Both — rare, treat as low (price spike down and up same candle)
      isPivotHigh = false;
    }

    // ── TREND-CONNECT LOGIC ──
    // Uptrend  = pivot-lows keep RISING  → connect LOWS.
    // Downtrend= pivot-highs keep FALLING → connect HIGHS.
    // A pivot that breaks its own trend flips the side (one crossing at the turn).
    if (isPivotLow) {
      swingPoints.push({ time: c.time, value: c.low, type: 'PIVOT_LOW' });
      if (c.low > (lastPivotLow ?? -Infinity)) {
        // higher low → uptrend intact (or starting) → ride the lows
        currentType  = 'LOW';
        currentLevel = c.low;
      } else if (currentType === 'LOW') {
        // lower low while riding lows → uptrend broken → still show this low,
        // but next opposite pivot will flip us to highs
        currentLevel = c.low;
      }
      lastPivotLow = c.low;
    } else if (isPivotHigh) {
      swingPoints.push({ time: c.time, value: c.high, type: 'PIVOT_HIGH' });
      if (c.high < (lastPivotHigh ?? Infinity)) {
        // lower high → downtrend intact (or starting) → ride the highs
        currentType  = 'HIGH';
        currentLevel = c.high;
      } else if (currentType === 'HIGH') {
        // higher high while riding highs → downtrend broken
        currentLevel = c.high;
      }
      lastPivotHigh = c.high;
    }

    // ── Push line point for pivot candle ──
    // Only push if we have a level established
    if (currentLevel !== null) {
      pushPoint(c.time, currentLevel);
    }
  }

  // ── Extend line to current candle ──
  // Fill remaining candles (last N candles not yet checked as pivots)
  if (currentLevel !== null) {
    for (let i = candles.length - n; i < candles.length; i++) {
      if (i < 0) continue;
      pushPoint(candles[i].time, currentLevel);
    }
  }

  // ── Current state ──
  const currentState = currentType === 'LOW'  ? 'uptrend'
                     : currentType === 'HIGH' ? 'downtrend'
                     : 'neutral';

  return { linePoints, swingPoints, currentState };
}